import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';

export default function AboutPage() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const goToContact = () => {
    navigate('/');
    setTimeout(() => {
      const el = document.getElementById('contact');
      if (el) el.scrollIntoView({ behavior: 'smooth' });
    }, 300);
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-white shadow-md' : 'bg-gray-900'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <div className="flex-shrink-0">
              <Link to="/">
                <img 
                  src="https://static.readdy.ai/image/82d44354838f3c049f6da9bd8c31e1b1/c02c3446a2b3e29ad54a764122882bc0.png" 
                  alt="NexBlocX" 
                  className="h-40"
                />
              </Link>
            </div>
            
            <div className="hidden md:flex items-center space-x-8">
              <Link 
                to="/"
                className={`text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${isScrolled ? 'text-gray-700 hover:text-gray-900' : 'text-white hover:text-gray-200'}`}
              >
                Home
              </Link>
              <Link 
                to="/about"
                className={`text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${isScrolled ? 'text-amber-600' : 'text-amber-400'}`}
              >
                About
              </Link>
              <Link 
                to="/#services"
                className={`text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${isScrolled ? 'text-gray-700 hover:text-gray-900' : 'text-white hover:text-gray-200'}`}
              >
                Services
              </Link>
              <Link 
                to="/resources"
                className={`text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${isScrolled ? 'text-gray-700 hover:text-gray-900' : 'text-white hover:text-gray-200'}`}
              >
                Resources
              </Link>
              <button 
                onClick={goToContact}
                className="bg-amber-500 text-gray-900 px-6 py-2.5 rounded-lg text-sm font-semibold hover:bg-amber-400 transition-colors cursor-pointer whitespace-nowrap inline-flex items-center"
              >
                Contact Us
              </button>
            </div>

            <button 
              className="md:hidden cursor-pointer"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              <i className={`${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl ${isScrolled ? 'text-gray-900' : 'text-white'}`}></i>
            </button>
          </div>
        </div>

        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-t">
            <div className="px-4 py-4 space-y-3">
              <Link 
                to="/"
                className="block w-full text-left px-4 py-2 text-gray-700 hover:bg-gray-50 rounded-lg cursor-pointer whitespace-nowrap"
              >
                Home
              </Link>
              <Link 
                to="/about"
                className="block w-full text-left px-4 py-2 text-amber-600 bg-amber-50 rounded-lg cursor-pointer whitespace-nowrap"
              >
                About
              </Link>
              <Link 
                to="/#services"
                className="block w-full text-left px-4 py-2 text-gray-700 hover:bg-gray-50 rounded-lg cursor-pointer whitespace-nowrap"
              >
                Services
              </Link>
              <Link 
                to="/resources"
                className="block w-full text-left px-4 py-2 text-gray-700 hover:bg-gray-50 rounded-lg cursor-pointer whitespace-nowrap"
              >
                Resources
              </Link>
              <button 
                onClick={goToContact}
                className="block w-full text-left px-4 py-2 bg-amber-500 text-gray-900 hover:bg-amber-400 rounded-lg font-semibold cursor-pointer whitespace-nowrap"
              >
                Contact Us
              </button>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 bg-gray-900">
        <div className="absolute inset-0">
          <img 
            src="https://public.readdy.ai/ai/img_res/5a6085206f8e57f08df49759d7cf7ce4.jpg"
            alt="ICF Construction"
            className="w-full h-full object-cover opacity-30"
          />
        </div>
        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">About Us</h1>
          <p className="text-xl text-gray-300">Building the Future of Concrete Construction</p>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Introduction */}
          <div className="mb-16">
            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              <strong>NexBlocX</strong> is a next-generation construction solutions company specializing in <strong>pre-engineered Insulated Concrete Form (ICF) systems</strong>. We are proud to be the <strong>official distributor of Polycrete Systems in Florida</strong>, delivering advanced concrete building technology to builders, architects, and developers seeking stronger, smarter, and more efficient structures.
            </p>
            <p className="text-lg text-gray-700 leading-relaxed">
              Our mission is simple: <strong>to simplify ICF construction while elevating performance, durability, and build quality</strong> across residential, commercial, and industrial projects.
            </p>
          </div>

          {/* Who We Are */}
          <div className="mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Who We Are</h2>
            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              NexBlocX was created to bridge the gap between innovative concrete technology and real-world construction execution. We understand that modern projects demand more than materials—they require precision, coordination, and expertise.
            </p>
            <p className="text-lg text-gray-700 leading-relaxed">
              By combining <strong>Polycrete Systems' proven ICF technology</strong> with NexBlocX's turn-key delivery model, we provide an integrated solution that reduces complexity, accelerates schedules, and increases confidence from design through installation.
            </p>
          </div>

          {/* Our Role */}
          <div className="mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Our Role as Polycrete Systems Distributor</h2>
            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              As the <strong>official Polycrete Systems distributor for Florida</strong>, NexBlocX provides:
            </p>
            <ul className="space-y-3 mb-6">
              <li className="flex items-start">
                <div className="w-6 h-6 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <i className="ri-checkbox-circle-fill text-amber-500 text-xl"></i>
                </div>
                <span className="ml-3 text-lg text-gray-700">Authorized ICF products and systems</span>
              </li>
              <li className="flex items-start">
                <div className="w-6 h-6 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <i className="ri-checkbox-circle-fill text-amber-500 text-xl"></i>
                </div>
                <span className="ml-3 text-lg text-gray-700">Technical expertise and system training</span>
              </li>
              <li className="flex items-start">
                <div className="w-6 h-6 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <i className="ri-checkbox-circle-fill text-amber-500 text-xl"></i>
                </div>
                <span className="ml-3 text-lg text-gray-700">Project-specific engineering coordination</span>
              </li>
              <li className="flex items-start">
                <div className="w-6 h-6 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <i className="ri-checkbox-circle-fill text-amber-500 text-xl"></i>
                </div>
                <span className="ml-3 text-lg text-gray-700">On-site support and installation guidance</span>
              </li>
            </ul>
            <p className="text-lg text-gray-700 leading-relaxed">
              Polycrete Systems is recognized for its high-performance ICF technology, and NexBlocX ensures that this system is deployed correctly, efficiently, and to its full potential on every project.
            </p>
          </div>

          {/* What We Do */}
          <div className="mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">What We Do</h2>
            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              NexBlocX offers a complete, turn-key ICF delivery service that includes:
            </p>
            <div className="space-y-6">
              <div className="bg-gray-50 rounded-lg p-6">
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Plan Conversion & Engineering</h3>
                <p className="text-gray-700">We convert architectural plans into optimized ICF layouts, ensuring structural integrity and code compliance.</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-6">
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Factory Pre-Cutting</h3>
                <p className="text-gray-700">All ICF components—including walls, openings, and corners—are precision-cut at the factory for fast, accurate assembly.</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-6">
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Coordinated Delivery</h3>
                <p className="text-gray-700">Materials arrive labeled, sequenced, and ready for installation—reducing delays and confusion on-site.</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-6">
                <h3 className="text-xl font-semibold text-gray-900 mb-2">On-Site Support</h3>
                <p className="text-gray-700">Our team provides guidance during installation to ensure proper technique and system performance.</p>
              </div>
            </div>
          </div>

          {/* Why ICF */}
          <div className="mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Why ICF?</h2>
            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              Insulated Concrete Forms (ICF) represent a smarter way to build. Compared to traditional wood or CMU construction, ICF delivers:
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex items-start bg-amber-50 rounded-lg p-4">
                <div className="w-8 h-8 flex items-center justify-center flex-shrink-0">
                  <i className="ri-shield-check-line text-2xl text-amber-600"></i>
                </div>
                <div className="ml-3">
                  <h4 className="font-semibold text-gray-900">Superior Strength</h4>
                  <p className="text-sm text-gray-600">Reinforced concrete core for maximum durability</p>
                </div>
              </div>
              <div className="flex items-start bg-amber-50 rounded-lg p-4">
                <div className="w-8 h-8 flex items-center justify-center flex-shrink-0">
                  <i className="ri-temp-cold-line text-2xl text-amber-600"></i>
                </div>
                <div className="ml-3">
                  <h4 className="font-semibold text-gray-900">Energy Efficiency</h4>
                  <p className="text-sm text-gray-600">Continuous insulation reduces energy costs</p>
                </div>
              </div>
              <div className="flex items-start bg-amber-50 rounded-lg p-4">
                <div className="w-8 h-8 flex items-center justify-center flex-shrink-0">
                  <i className="ri-tornado-line text-2xl text-amber-600"></i>
                </div>
                <div className="ml-3">
                  <h4 className="font-semibold text-gray-900">Disaster Resistance</h4>
                  <p className="text-sm text-gray-600">Withstands hurricanes, fire, and impact</p>
                </div>
              </div>
              <div className="flex items-start bg-amber-50 rounded-lg p-4">
                <div className="w-8 h-8 flex items-center justify-center flex-shrink-0">
                  <i className="ri-volume-mute-line text-2xl text-amber-600"></i>
                </div>
                <div className="ml-3">
                  <h4 className="font-semibold text-gray-900">Sound Reduction</h4>
                  <p className="text-sm text-gray-600">Excellent acoustic insulation properties</p>
                </div>
              </div>
              <div className="flex items-start bg-amber-50 rounded-lg p-4">
                <div className="w-8 h-8 flex items-center justify-center flex-shrink-0">
                  <i className="ri-time-line text-2xl text-amber-600"></i>
                </div>
                <div className="ml-3">
                  <h4 className="font-semibold text-gray-900">Faster Build Times</h4>
                  <p className="text-sm text-gray-600">Pre-cut systems accelerate construction</p>
                </div>
              </div>
              <div className="flex items-start bg-amber-50 rounded-lg p-4">
                <div className="w-8 h-8 flex items-center justify-center flex-shrink-0">
                  <i className="ri-money-dollar-circle-line text-2xl text-amber-600"></i>
                </div>
                <div className="ml-3">
                  <h4 className="font-semibold text-gray-900">Lower Lifecycle Costs</h4>
                  <p className="text-sm text-gray-600">Reduced maintenance and insurance savings</p>
                </div>
              </div>
            </div>
          </div>

          {/* Who We Serve */}
          <div className="mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Who We Serve</h2>
            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              NexBlocX works with a wide range of construction professionals, including:
            </p>
            <ul className="space-y-3">
              <li className="flex items-start">
                <div className="w-6 h-6 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <i className="ri-arrow-right-s-fill text-amber-500 text-xl"></i>
                </div>
                <span className="ml-3 text-lg text-gray-700">General contractors and builders</span>
              </li>
              <li className="flex items-start">
                <div className="w-6 h-6 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <i className="ri-arrow-right-s-fill text-amber-500 text-xl"></i>
                </div>
                <span className="ml-3 text-lg text-gray-700">Architects and engineers</span>
              </li>
              <li className="flex items-start">
                <div className="w-6 h-6 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <i className="ri-arrow-right-s-fill text-amber-500 text-xl"></i>
                </div>
                <span className="ml-3 text-lg text-gray-700">Real estate developers</span>
              </li>
              <li className="flex items-start">
                <div className="w-6 h-6 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <i className="ri-arrow-right-s-fill text-amber-500 text-xl"></i>
                </div>
                <span className="ml-3 text-lg text-gray-700">Owner-builders and custom home clients</span>
              </li>
              <li className="flex items-start">
                <div className="w-6 h-6 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <i className="ri-arrow-right-s-fill text-amber-500 text-xl"></i>
                </div>
                <span className="ml-3 text-lg text-gray-700">Commercial and industrial project teams</span>
              </li>
            </ul>
          </div>

          {/* Our Commitment */}
          <div className="bg-gray-900 rounded-2xl p-8 md:p-12 text-center">
            <h2 className="text-3xl font-bold text-white mb-6">Our Commitment</h2>
            <p className="text-lg text-gray-300 leading-relaxed mb-8">
              At NexBlocX, we are committed to making ICF construction accessible, efficient, and reliable. Whether you're building a single-family home or a multi-story commercial structure, we bring the expertise, materials, and support needed to deliver a high-performance result.
            </p>
            <button 
              onClick={goToContact}
              className="inline-flex items-center bg-amber-500 text-gray-900 px-8 py-4 rounded-lg text-lg font-bold hover:bg-amber-400 transition-all transform hover:scale-105 cursor-pointer whitespace-nowrap"
            >
              Get Started Today
              <i className="ri-arrow-right-line ml-2"></i>
            </button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="relative bg-gray-900 text-white py-16 overflow-hidden min-h-[500px]">
        <div className="absolute inset-0">
          <img 
            src="https://static.readdy.ai/image/82d44354838f3c049f6da9bd8c31e1b1/5f47c77c836e004333a2d82b946d5e21.jpeg" 
            alt="Footer Background" 
            className="w-full h-full object-cover object-center absolute inset-0"
          />
          <div className="absolute inset-0 bg-black/40"></div>
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-12 mb-12 items-start">
            <div className="pt-1">
              <img 
                src="https://public.readdy.ai/ai/img_res/3d38b249-c438-4355-9c41-27c4e7e91c1d.png" 
                alt="NexBlocX" 
                className="h-22"
              />
            </div>

            <div>
              <h4 className="text-lg font-bold mb-4">Company</h4>
              <ul className="space-y-2">
                <li>
                  <Link to="/about" className="text-gray-400 hover:text-white transition-colors cursor-pointer whitespace-nowrap">
                    About
                  </Link>
                </li>
                <li>
                  <button onClick={goToContact} className="text-gray-400 hover:text-white transition-colors cursor-pointer whitespace-nowrap">
                    Contact
                  </button>
                </li>
              </ul>
            </div>

            <div>
              <h4 className="text-lg font-bold mb-4">Why NexblocX</h4>
              <ul className="space-y-2">
                <li>
                  <Link to="/#features" className="text-gray-400 hover:text-white transition-colors cursor-pointer whitespace-nowrap">
                    Our System
                  </Link>
                </li>
                <li>
                  <Link to="/#services" className="text-gray-400 hover:text-white transition-colors cursor-pointer whitespace-nowrap">
                    Building with Steel
                  </Link>
                </li>
              </ul>
            </div>

            <div>
              <h4 className="text-lg font-bold mb-4">Resources</h4>
              <ul className="space-y-2">
                <li><a href="#" className="text-gray-400 hover:text-white transition-colors">Project Gallery</a></li>
                <li><a href="#" className="text-gray-400 hover:text-white transition-colors">FAQ</a></li>
                <li><a href="#" className="text-gray-400 hover:text-white transition-colors">Blog</a></li>
              </ul>
            </div>
          </div>

          <div className="border-t border-white/20 pt-8 mt-8">
            <div className="flex flex-col md:flex-row justify-between items-center">
              <div className="flex flex-wrap items-center justify-center md:justify-start gap-4 mb-4 md:mb-0 text-sm text-gray-400">
                <span>All rights reserved 2026 NexblocX Polycrete Systems</span>
                <span className="hidden md:inline">|</span>
                <Link to="/privacy" className="hover:text-white transition-colors">
                  Privacy Policy
                </Link>
                <span>|</span>
                <Link to="/terms" className="hover:text-white transition-colors">
                  Terms & Conditions
                </Link>
                <span>|</span>
                <a href="https://readdy.ai/?ref=logo" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">
                  Powered by Readdy
                </a>
              </div>

              <div className="flex items-center space-x-4">
                <a href="https://linkedin.com" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-white transition-colors" aria-label="LinkedIn">
                  <i className="ri-linkedin-fill text-xl"></i>
                </a>
                <a href="https://youtube.com" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-white transition-colors" aria-label="YouTube">
                  <i className="ri-youtube-fill text-xl"></i>
                </a>
                <a href="https://facebook.com" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-white transition-colors" aria-label="Facebook">
                  <i className="ri-facebook-fill text-xl"></i>
                </a>
                <a href="https://instagram.com" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-white transition-colors" aria-label="Instagram">
                  <i className="ri-instagram-fill text-xl"></i>
                </a>
                <a href="https://x.com" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-white transition-colors" aria-label="X">
                  <i className="ri-twitter-x-fill text-xl"></i>
                </a>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
